import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Company } from '../../../../../models/company.model';
import { Pagination, parsePagination } from '../../../../../models/pagination.model';
import { CurrentDateService } from '../../../../../services/current-date.service';
import { addDays, endOfDay, startOfDay } from '../../../../../utilities/date';
import { Invoice } from '../../models/invoice.model';
import { InvoiceService } from '../../services/invoice.service';

/**
 * Modal usado para buscar y seleccionar {@link Invoice|Invoices} por fecha de
 * vencimiento, monto, referencia o emisor, en lugar de solo por número exacto
 * de factura.
 *
 * ### Componentes de UI relacionados:
 * - [[CreateBarterComponent]]
 */
@Component({
  selector: 'ag-invoices-picker-modal',
  templateUrl: './invoices-picker-modal.component.html',
  styleUrls: ['./invoices-picker-modal.component.scss']
})
export class InvoicesPickerModalComponent implements OnDestroy {

  @ViewChild('modal', { static: true }) private readonly modal: TemplateRef<any>;

  @Input() public company: Company;
  @Input() public recipient: Company;
  /** Facturas ya agregadas al formulario; se muestran deshabilitadas y no se pueden volver a seleccionar. */
  @Input() public excluded: Invoice[] = [];
  /** Cantidad máxima de facturas permitidas en total (excluidas + seleccionadas). */
  @Input() public maxItems: number = 10;

  @Output() readonly selection = new EventEmitter<Invoice[]>();

  public invoices: Invoice[];
  public selected: Invoice[] = [];
  public loading: boolean;
  public pagination: Pagination;
  public sortBy: string = 'due_date';

  // Filtros
  public dueDateRange: Date[];
  public overdueOnly: boolean;
  public dueInDays: number;
  public balanceMin: number;
  public balanceMax: number;
  public reference: string;
  public recipientName: string;

  private page: number = 1;
  private readonly perPage: number = 50;
  private modalRef: BsModalRef;
  private modalSub: Subscription;
  private loadSub: Subscription;
  private readonly filtersChanged = new Subject<void>();
  private subscriptions: Subscription[] = [];

  constructor(
    private modalService: BsModalService,
    private invoiceService: InvoiceService,
    private currentDateService: CurrentDateService
  ) {
    this.subscriptions.push(this.filtersChanged.pipe(debounceTime(400)).subscribe(() => this.loadData(true)));
  }

  public open(): void {
    this.reset();
    this.loadData(true);
    this.openModal(this.modal);
  }

  private reset(): void {
    this.selected = [];
    this.page = 1;
    this.invoices = undefined;
    this.resetFilters();
  }

  private resetFilters(): void {
    this.dueDateRange = undefined;
    this.overdueOnly = false;
    this.dueInDays = undefined;
    this.balanceMin = undefined;
    this.balanceMax = undefined;
    this.reference = undefined;
    this.recipientName = undefined;
    this.sortBy = 'due_date';
  }

  /** Se llama desde los inputs de filtro de texto/número; espera (debounce) antes de recargar. */
  public onFiltersChange(): void {
    this.filtersChanged.next();
  }

  public setOverdue(): void {
    this.overdueOnly = !this.overdueOnly;
    this.dueInDays = undefined;
    this.dueDateRange = undefined;
    this.loadData(true);
  }

  public setDueInDays(days: number): void {
    this.dueInDays = this.dueInDays === days ? undefined : days;
    this.overdueOnly = false;
    this.dueDateRange = undefined;
    this.loadData(true);
  }

  public onDueDateRangeChange(): void {
    this.overdueOnly = false;
    this.dueInDays = undefined;
    this.loadData(true);
  }

  public clearFilters(): void {
    this.resetFilters();
    this.loadData(true);
  }

  public sort(key: string): void {
    this.sortBy = this.sortBy === key ? '-' + key : key;
    this.loadData(true);
  }

  public loadMore(): void {
    this.page++;
    this.loadData(false);
  }

  private loadData(reset: boolean): void {
    if (reset) this.page = 1;
    this.loading = true;

    const today = this.currentDateService.get();

    const filters: any = {
      page: this.page,
      per_page: this.perPage,
      order_by: this.sortBy,
      'filters[settled]': 'false'
    };

    if (this.recipient) {
      filters['filters[recipient.name]'] = 'is:' + this.recipient.name;
    } else if (this.recipientName) {
      filters['filters[recipient.name]'] = 'contains:' + this.recipientName;
    }

    if (this.overdueOnly) {
      filters['filters[due_date]'] = 'before:' + endOfDay(today).toISOString();
    } else if (this.dueInDays) {
      filters['filters[due_date]'] = 'between:' + startOfDay(today).toISOString() + ',' + endOfDay(addDays(today, this.dueInDays)).toISOString();
    } else if (this.dueDateRange?.length === 2 && this.dueDateRange[0] && this.dueDateRange[1]) {
      filters['filters[due_date]'] = 'between:' + startOfDay(this.dueDateRange[0]).toISOString() + ',' + endOfDay(this.dueDateRange[1]).toISOString();
    }

    if (this.balanceMin) filters['filters[balance_min]'] = 'gte:' + this.balanceMin;
    if (this.balanceMax) filters['filters[balance_max]'] = 'lte:' + this.balanceMax;
    if (this.reference) filters['filters[reference]'] = 'contains:' + this.reference;
    // La empresa logueada siempre es la emisora de estas facturas (ver InvoiceController::create).
    filters['filters[company.name]'] = 'is:' + this.company.name;

    this.loadSub?.unsubscribe();
    this.loadSub = this.invoiceService.search(this.company.id, filters).subscribe(response => {
      this.invoices = reset ? response.body : [...(this.invoices || []), ...response.body];
      this.pagination = parsePagination(response.headers);
      this.loading = false;
    });
  }

  public isExcluded(invoice: Invoice): boolean {
    return this.excluded?.some(i => i.id === invoice.id);
  }

  public isSelected(invoice: Invoice): boolean {
    return this.selected.some(i => i.id === invoice.id);
  }

  public get maxReached(): boolean {
    return (this.excluded?.length || 0) + this.selected.length >= this.maxItems;
  }

  public toggle(invoice: Invoice): void {
    if (this.isExcluded(invoice)) return;

    const index = this.selected.findIndex(i => i.id === invoice.id);
    if (index !== -1) {
      this.selected.splice(index, 1);
    } else {
      if (this.maxReached) return;
      this.selected.push(invoice);
    }
  }

  public get selectedBalance(): number {
    return this.selected.reduce((sum, invoice) => sum + (invoice.balance - (invoice.settled || 0)), 0);
  }

  public confirm(): void {
    this.selection.emit(this.selected);
    this.closeModal();
  }

  /** Disparador genérico del Modal. */
  private openModal(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template, { class: 'modal-lg' });

    this.modalSub = this.modalRef.onHide.subscribe(() => {
      this.modalSub.unsubscribe();
      this.modalRef = undefined;
      this.invoices = undefined;
    });
  }

  public closeModal(): void {
    if (this.modalRef) this.modalRef.hide();
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.closeModal();
    this.loadSub?.unsubscribe();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
